import React, { useCallback, useEffect, useState } from "react";
import {
  fetchAdminInventory,
  createInventoryAssignment,
  updateInventoryAssignment,
} from "../../api/adminData";

function roomKey(roomId) {
  return roomId && String(roomId).trim() ? String(roomId).trim() : "";
}

function findAssignmentForSlot(assignments, itemId, unitId, roomId) {
  const rk = roomKey(roomId);
  return (assignments || []).find(
    (a) =>
      String(a.inventory_item_id) === String(itemId) &&
      String(a.unit_id) === String(unitId) &&
      roomKey(a.room_id) === rk
  );
}

export default function InventoryAssignModal({
  open,
  onClose,
  unitId,
  unitType,
  rooms = [],
  existingAssignments = [],
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadErr, setLoadErr] = useState("");
  const [submitErr, setSubmitErr] = useState("");
  const [freeItems, setFreeItems] = useState([]);
  const [qtyByItemId, setQtyByItemId] = useState({});
  const [roomByItemId, setRoomByItemId] = useState({});

  const showRoomCol =
    String(unitType || "").trim() === "Co-Living" && Array.isArray(rooms) && rooms.length > 0;

  const resetForm = useCallback(() => {
    setQtyByItemId({});
    setRoomByItemId({});
    setSubmitErr("");
    setLoadErr("");
  }, []);

  useEffect(() => {
    if (!open || !unitId) return;
    resetForm();
    setLoading(true);
    setLoadErr("");
    fetchAdminInventory({ limit: 200 })
      .then((data) => {
        const list = Array.isArray(data?.items) ? data.items : [];
        const free = list.filter((it) => Number(it.available ?? 0) > 0);
        setFreeItems(free);
        const initRoom = {};
        for (const it of free) {
          initRoom[it.id] = "";
        }
        setRoomByItemId(initRoom);
      })
      .catch((e) => {
        setLoadErr(e?.message || "Inventar konnte nicht geladen werden.");
        setFreeItems([]);
      })
      .finally(() => setLoading(false));
  }, [open, unitId, resetForm]);

  const handleQtyChange = (itemId, raw) => {
    setQtyByItemId((prev) => ({ ...prev, [itemId]: raw }));
  };

  const handleRoomChange = (itemId, value) => {
    setRoomByItemId((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!unitId || saving) return;
    setSubmitErr("");

    const ops = [];
    for (const it of freeItems) {
      const q = Math.max(0, parseInt(String(qtyByItemId[it.id] ?? "0"), 10) || 0);
      if (q < 1) continue;
      const avail = Number(it.available ?? 0);
      if (q > avail) {
        setSubmitErr(`Menge zu hoch für «${it.name || it.inventory_number}» (max. ${avail} frei).`);
        return;
      }
      const selectedRoom = showRoomCol ? String(roomByItemId[it.id] ?? "").trim() : "";
      const roomId = selectedRoom ? selectedRoom : null;
      const existing = findAssignmentForSlot(existingAssignments, it.id, unitId, roomId);
      ops.push({ item: it, qty: q, roomId, existing });
    }

    if (ops.length === 0) {
      setSubmitErr("Bitte mindestens eine Menge größer 0 eintragen.");
      return;
    }

    setSaving(true);
    let anySaved = false;
    try {
      for (const { item, qty, roomId, existing } of ops) {
        if (existing) {
          const nextQty = Number(existing.quantity ?? 0) + qty;
          await updateInventoryAssignment(existing.id, {
            unit_id: String(unitId),
            room_id: roomId,
            quantity: nextQty,
            notes: (existing.notes && String(existing.notes).trim()) || null,
          });
        } else {
          await createInventoryAssignment(item.id, {
            unit_id: String(unitId),
            room_id: roomId,
            quantity: qty,
            notes: null,
          });
        }
        anySaved = true;
      }
      resetForm();
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      const base = err?.message || "Zuordnung fehlgeschlagen.";
      setSubmitErr(
        anySaved
          ? `Speichern bei einer Position gestoppt. Bereits gespeicherte Zuordnungen wurden übernommen. ${base}`
          : base
      );
      if (onSuccess) onSuccess();
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4 dark:bg-black/60">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-[14px] border border-black/10 bg-white dark:border-white/[0.07] dark:bg-[#141824]">
        <div className="border-b border-black/10 px-5 py-4 dark:border-white/[0.08]">
          <h4 className="m-0 text-lg font-semibold text-[#0f172a] dark:text-[#eef2ff]">Inventar zuordnen</h4>
          <p className="mt-1 mb-0 text-[11px] text-[#64748b] dark:text-[#93a4bf]">
            Nur Artikel mit freiem Bestand. Pro Artikel und Raum-Slot wird eine Zuordnung erweitert oder neu angelegt.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex max-h-[calc(90vh-8rem)] flex-col">
          <div className="min-h-0 flex-1 overflow-auto px-5 py-3">
            {loadErr ? <p className="text-sm text-[#f87171]">{loadErr}</p> : null}
            {loading ? (
              <p className="text-[12px] text-[#64748b] dark:text-[#93a4bf]">Lade Inventar …</p>
            ) : freeItems.length === 0 ? (
              <p className="text-[12px] text-[#64748b] dark:text-[#93a4bf]">
                Keine Artikel mit freiem Bestand.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-[#0f172a] dark:text-[#eef2ff]">
                  <thead>
                    <tr className="border-b border-black/10 text-[9px] font-bold uppercase tracking-[0.8px] text-[#64748b] dark:border-white/[0.08] dark:text-[#6b7a9a]">
                      <th className="py-2 pr-3">Name</th>
                      <th className="py-2 pr-3">Kategorie</th>
                      <th className="py-2 pr-3 text-right">Gesamt</th>
                      <th className="py-2 pr-3 text-right">Frei</th>
                      {showRoomCol ? <th className="py-2 pr-3">Raum</th> : null}
                      <th className="py-2 pr-2 text-right">Menge</th>
                    </tr>
                  </thead>
                  <tbody>
                    {freeItems.map((it) => (
                      <tr
                        key={it.id}
                        className="border-b border-black/5 dark:border-white/[0.06]"
                      >
                        <td className="py-2 pr-3">
                          <span className="text-[11px] text-[#64748b] dark:text-[#93a4bf]">
                            {it.inventory_number}
                          </span>{" "}
                          {it.name}
                        </td>
                        <td className="py-2 pr-3 text-[#64748b] dark:text-[#93a4bf]">
                          {it.category || "—"}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">{it.total_quantity ?? "—"}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{it.available ?? 0}</td>
                        {showRoomCol ? (
                          <td className="py-2 pr-3">
                            <select
                              value={roomByItemId[it.id] ?? ""}
                              onChange={(e) => handleRoomChange(it.id, e.target.value)}
                              className="w-full min-w-[120px] rounded-lg border border-black/10 bg-slate-100 px-2 py-1 text-xs text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
                            >
                              <option value="">— ganze Unit —</option>
                              {rooms.map((r) => {
                                const rid = r.id || r.roomId;
                                return (
                                  <option key={rid} value={rid}>
                                    {r.name || r.roomName || rid}
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                        ) : null}
                        <td className="py-2 pr-2 text-right">
                          <input
                            type="number"
                            min={1}
                            max={Number(it.available ?? 0)}
                            value={qtyByItemId[it.id] ?? ""}
                            placeholder="0"
                            onChange={(e) => handleQtyChange(it.id, e.target.value)}
                            className="w-[4.5rem] rounded-lg border border-black/10 bg-slate-100 px-2 py-1 text-right text-xs text-[#0f172a] dark:border-white/[0.08] dark:bg-[#111520] dark:text-[#eef2ff]"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {submitErr ? (
            <div className="px-5">
              <p className="mb-2 text-sm text-[#f87171]">{submitErr}</p>
            </div>
          ) : null}

          <div className="flex justify-end gap-2 border-t border-black/10 px-5 py-4 dark:border-white/[0.08]">
            <button
              type="button"
              disabled={saving}
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="rounded-lg border border-black/10 px-4 py-2 text-sm text-[#0f172a] hover:bg-slate-100 dark:border-white/[0.12] dark:text-[#eef2ff] dark:hover:bg-white/[0.04]"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={saving || loading || freeItems.length === 0}
              className="rounded-lg bg-[#5b8cff] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {saving ? "Speichern …" : "Zuordnen"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
